export const DOCS_LOCALES = ['en', 'ja', 'es', 'ar', 'ko'] as const

export type DocsLocale = (typeof DOCS_LOCALES)[number]

/**
 * Coerce an arbitrary locale string (e.g. from `useLocale()` or a route
 * parameter) into a `DocsLocale`. Falls back to `'en'` for unknown locales so
 * the docs UI always has a valid map key.
 */
export function toDocsLocale(locale: string): DocsLocale {
  return (DOCS_LOCALES as readonly string[]).includes(locale) ? (locale as DocsLocale) : 'en'
}

export interface DocsSection {
  id: string
  title: string
  order: number
}

export interface DocsPage {
  slug: string
  title: string
  section: string
  order: number
  description: string
  overview: string | null
  /**
   * Label for the left nav, when the page heading would read redundantly there.
   * The sidebar nests pages under their section, so a page titled "Sets
   * overview" renders as "Sets › Sets overview". Set `navTitle: 'Overview'` and
   * the heading stays specific while the nav stays short. Defaults to `title`.
   */
  navTitle?: string
  /**
   * Extra terms indexed by Learn search when a route renders a rich component
   * instead of the table-driven `overview` field.
   */
  searchText?: string
  featureIds?: string[]
  /**
   * Standalone screenshots rendered below the overview. Inline `![alt](src)`
   * markers inside `overview` are also supported (and preferred). Paths use the
   * `/docs-imgs/...` prefix and are resolved via `docsAsset()`.
   */
  screenshots?: string[]
  sourceHash?: string
  source_en_hash?: string
  generatedAt?: string
  manualOverride?: boolean
}

export interface DocsPageResolved extends DocsPage {
  sectionTitle: string
  shortcuts: DocsShortcut[]
  glossaryTerms: DocsGlossaryTerm[]
  relatedPages: { slug: string; title: string }[]
  screenshots: string[]
  videoUrl: string | null
  heroScreenshot: string | null
}

export interface DocsShortcut {
  keys: string
  label: string
}

export interface DocsGlossaryTerm {
  term: string
  definition: string
}

export interface DocsTocEntry {
  id: string
  label: string
  level: number
}

export interface DocsPageTranslation {
  title?: string
  description?: string
  overview?: string | null
  source_en_hash: string
}

export type DocsTranslationState = 'current' | 'missing' | 'stale'

export interface DocsTranslationStatus {
  slug: string
  locale: Exclude<DocsLocale, 'en'>
  title: string
  status: DocsTranslationState
  expected_source_en_hash: string
  source_en_hash?: string
}
